'use client';

import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Send, Ban, ShieldOff, Loader2, MessageCircle } from 'lucide-react';
import { RealtimeChannel } from '@supabase/supabase-js';

interface Message {
  id: string;
  course_id: string;
  user_id: string;
  message: string;
  created_at: string;
  profiles: {
    full_name: string;
    role: string;
  };
}

interface BannedUser {
  id: string;
  user_id: string;
  reason: string | null;
  profiles: {
    full_name: string;
    email: string;
  };
}

interface CourseChatProps {
  courseId: string;
  isTeacher: boolean;
}

export function CourseChat({ courseId, isTeacher }: CourseChatProps) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [bannedUsers, setBannedUsers] = useState<BannedUser[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [userToBan, setUserToBan] = useState<{ id: string; name: string } | null>(null);
  const [banReason, setBanReason] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (profile) {
      fetchMessages();
      checkIfBanned();
      if (isTeacher) {
        fetchBannedUsers();
      }
      subscribeToMessages();
    }

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [profile, courseId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const subscribeToMessages = () => {
    const channel = supabase
      .channel(`course-chat-${courseId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'course_chat_messages',
          filter: `course_id=eq.${courseId}`,
        },
        async (payload) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name, role')
            .eq('id', payload.new.user_id)
            .single();

          const newMsg = {
            ...payload.new,
            profiles: profileData,
          } as Message;

          setMessages((prev) => [...prev, newMsg]);
        }
      )
      .subscribe();

    channelRef.current = channel;
  };

  const fetchMessages = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('course_chat_messages')
      .select(`
        *,
        profiles:user_id (
          full_name,
          role
        )
      `)
      .eq('course_id', courseId)
      .order('created_at', { ascending: true })
      .limit(100);

    if (data) {
      setMessages(data);
    }
    setLoading(false);
  };

  const checkIfBanned = async () => {
    const { data } = await supabase
      .from('course_chat_bans')
      .select('id')
      .eq('course_id', courseId)
      .eq('user_id', profile?.id)
      .maybeSingle();

    setIsBanned(!!data);
  };

  const fetchBannedUsers = async () => {
    const { data } = await supabase
      .from('course_chat_bans')
      .select(`
        *,
        profiles:user_id (
          full_name,
          email
        )
      `)
      .eq('course_id', courseId);

    if (data) {
      setBannedUsers(data);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || isBanned) return;

    setSending(true);

    const { error } = await supabase.from('course_chat_messages').insert({
      course_id: courseId,
      user_id: profile?.id,
      message: newMessage.trim(),
    });

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile inviare il messaggio',
        variant: 'destructive',
      });
    } else {
      setNewMessage('');
    }

    setSending(false);
  };

  const handleBanUser = (userId: string, userName: string) => {
    setUserToBan({ id: userId, name: userName });
    setBanReason('');
    setShowBanDialog(true);
  };

  const confirmBan = async () => {
    if (!userToBan) return;

    const { error } = await supabase.from('course_chat_bans').insert({
      course_id: courseId,
      user_id: userToBan.id,
      banned_by: profile?.id,
      reason: banReason.trim() || null,
    });

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile bannare l\'utente',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Utente bannato',
        description: `${userToBan.name} non può più scrivere in chat`,
      });
      fetchBannedUsers();
      setShowBanDialog(false);
    }
  };

  const handleUnbanUser = async (userId: string, userName: string) => {
    if (!confirm(`Vuoi sbannare ${userName} dalla chat?`)) return;

    const { error } = await supabase
      .from('course_chat_bans')
      .delete()
      .eq('course_id', courseId)
      .eq('user_id', userId);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile sbannare l\'utente',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Utente sbannato',
        description: `${userName} può di nuovo scrivere in chat`,
      });
      fetchBannedUsers();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Chat del corso
              </CardTitle>
              <CardDescription>
                {isBanned
                  ? 'Sei stato bannato dalla chat'
                  : 'Chatta con il docente e gli altri studenti'}
              </CardDescription>
            </div>
            {isTeacher && bannedUsers.length > 0 && (
              <Badge variant="destructive">
                {bannedUsers.length} bannati
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <ScrollArea ref={scrollRef} className="h-96 pr-4">
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nessun messaggio ancora</p>
                  <p className="text-sm">Inizia la conversazione!</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.user_id === profile?.id;
                  const isTeacherMsg = msg.profiles?.role === 'docente';

                  return (
                    <div
                      key={msg.id}
                      className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}
                    >
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        <AvatarFallback className={isTeacherMsg ? 'bg-blue-600 text-white' : 'bg-gray-300'}>
                          {getInitials(msg.profiles?.full_name || 'U')}
                        </AvatarFallback>
                      </Avatar>
                      <div className={`flex-1 ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-semibold ${isOwn ? 'order-2' : ''}`}>
                            {msg.profiles?.full_name || 'Utente'}
                          </span>
                          {isTeacherMsg && (
                            <Badge variant="default" className="text-xs">
                              Docente
                            </Badge>
                          )}
                          {isTeacher && !isOwn && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-xs"
                              onClick={() => handleBanUser(msg.user_id, msg.profiles?.full_name || 'Utente')}
                            >
                              <Ban className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <div
                          className={`rounded-lg px-4 py-2 max-w-md ${
                            isOwn
                              ? 'bg-blue-600 text-white'
                              : isTeacherMsg
                              ? 'bg-green-100 border border-green-200'
                              : 'bg-gray-100'
                          }`}
                        >
                          <p className="text-sm break-words">{msg.message}</p>
                        </div>
                        <span className="text-xs text-gray-500 mt-1">
                          {new Date(msg.created_at).toLocaleTimeString('it-IT', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>

          {isBanned ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <Ban className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <p className="text-red-900 font-semibold">Sei stato bannato dalla chat</p>
              <p className="text-sm text-red-700">Non puoi più inviare messaggi</p>
            </div>
          ) : (
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                placeholder="Scrivi un messaggio..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={sending}
                maxLength={500}
              />
              <Button type="submit" disabled={sending || !newMessage.trim()}>
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {isTeacher && bannedUsers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              Utenti bannati
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {bannedUsers.map((ban) => (
                <div
                  key={ban.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-semibold text-sm">{ban.profiles.full_name}</p>
                    <p className="text-xs text-gray-600">{ban.profiles.email}</p>
                    {ban.reason && (
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="font-semibold">Motivo:</span> {ban.reason}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUnbanUser(ban.user_id, ban.profiles.full_name)}
                  >
                    <ShieldOff className="w-4 h-4 mr-2" />
                    Sbanna
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Banna utente dalla chat</DialogTitle>
            <DialogDescription>
              Stai per bannare <strong>{userToBan?.name}</strong> dalla chat del corso
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ban-reason">Motivo (opzionale)</Label>
              <Textarea
                id="ban-reason"
                placeholder="Es: Linguaggio inappropriato, spam, etc."
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBanDialog(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={confirmBan}>
              <Ban className="w-4 h-4 mr-2" />
              Banna utente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
